import React from "react";
import { IConfigStorage } from "../../rule-engine-core/Common/IConfigStorage";
import { DataUtilities } from "../../rule-engine-core/Common/DataUtilities";
import { IEditable } from "../../CommonUI/IEditable";
import { ExecutionData } from "../../rule-engine-core/Common/Data/ExecutionData/ExecutionData";
import { IFormDataAccessor } from "../Utilities/IFormDataAccessor";
import {
    IDesignValidationProvider,
    IRuntimeValidationProvider,
    ValidationIssue,
} from "../../rule-engine-core/Common/IDesignValidationProvider";
import { AutoBind } from "../../rule-engine-core/Common/AutoBind";
import { FormContext } from "../Utilities/FormContext";
import { ControlRenderContext } from "../Utilities/ControlRenderContext";
import { StateManager } from "../../rule-engine-core/Common/StateManager";
import { IDataDefinition } from "../../rule-engine-core/Common/Data/Definitions/IDataDefinition";
import type { AppGlueAny } from "../../rule-engine-core/Common/AppGlueAny";

export abstract class FormControl
    extends React.Component
    implements IConfigStorage, IEditable, IFormDataAccessor, IDesignValidationProvider, IRuntimeValidationProvider
{
    abstract readonly __type: string;

    id: string;
    ref: React.RefObject<AppGlueAny>;
    canFocus: boolean;

    // not serialized
    private _formContext?: FormContext;

    constructor() {
        super({});

        // generates ID to be a guid
        this.id = DataUtilities.generateUniqueId();
        this.ref = React.createRef();
        this.canFocus = true;

        StateManager.addObserver(this, () => {
            this.getFormContext()!.computeDesignValidationIssues();
        });
    }

    get controlRenderContext(): ControlRenderContext | null | undefined {
        return this.getFormContext()?.getControlContext(this);
    }

    // to implement
    abstract getDesignValidationIssues(): ValidationIssue[];

    abstract getRuntimeValidationIssues(): ValidationIssue[];

    abstract getAddedData(): IDataDefinition | IDataDefinition[] | undefined;

    focusControl(): void {
        this.ref.current.focus();
    }

    abstract renderEditUI(): JSX.Element | null;

    getFormContext(): FormContext | undefined {
        return this._formContext;
    }

    setFormContext(value: FormContext | undefined): void {
        if (!value) throw new Error("cannot set edit context to null (base control)");

        this._formContext = value;
    }

    getStorageData(): object {
        return DataUtilities.addStorageData(this, undefined, "__type", "id", "canFocus");
    }

    setStorageData(data: object): void {
        DataUtilities.setStorageData(this, data, "__type", "id", "canFocus");
    }

    // forces refresh of entire designer (expensive)
    @AutoBind
    designerUpdate(): void {
        this.getFormContext()?.refreshDesigner();
    }

    isDesignSelected(): boolean {
        if (this.getFormContext()) {
            return this.getFormContext()!.getControlContext(this).selected;
        }

        return false;
    }

    selectInDesigner(): void {
        //        console.log('calling select in designer')
        if (this.getFormContext()) {
            this.getFormContext()!.selectControl(this.id);
        }
    }

    unselectInDesigner(): void {
        if (this.getFormContext()) {
            this.getFormContext()!.unSelectControl();
        }
    }

    getFormData(): ExecutionData | undefined | null {
        return this.getFormContext()?.getFormData();
    }

    getFormDataValue(fieldName: string): any {
        return this.getFormContext()?.getFormDataValue(fieldName);
    }

    setFormData(value: ExecutionData): void {
        return this.getFormContext()?.setFormData(value);
    }

    setFormDataValue(fieldName: string, value: any): void {
        this.getFormContext()?.setFormDataValue(fieldName, value);
    }

    toString(): string {
        let type = Reflect.get(this, "__type") as string;

        if (type && type.startsWith("X")) {
            type = type.substring(1);
        }

        let valueName = Reflect.get(this, "valueName") as string;

        if (type && valueName) {
            return type + " [" + valueName + "]";
        } else if (type) {
            return type + " [" + valueName + "]";
        } else if (valueName) {
            return valueName;
        }

        return "";
    }
}
